/**
 * Task Visual Hierarchy V2 — representación visual vinculante de una tarea.
 *
 * Orden jerárquico obligatorio (BHM-CDX-DIR-108):
 *   1. PROJECT NAME   — máxima prioridad visual
 *   2. AREA / PROCESS — segunda jerarquía
 *   3. SHORT TASK NAME— identificación operativa (taskCode + title)
 *   4. RESPONSIBLE    — visible cuando el espacio lo permite
 *   5. LOCATION       — visible si existe
 *   6. DESCRIPTION    — primero en sacrificarse/truncarse
 *
 * Ausencia = omisión visual limpia (nunca «Sin ubicación» / «N/A»).
 * Variantes: FULL (6 niveles) · COMPACT (description sacrificada primero) ·
 *            MINIMAL (Project/Process/Task; resto solo si el contexto permite).
 * Sin colores hardcoded: tokens del tema (light/dark preservados).
 */
import React from 'react';

export type TaskHierarchyVariant = 'full' | 'compact' | 'minimal';

export interface TaskHierarchyData {
  projectName?: string | null;
  /** Área / proceso. */
  processName?: string | null;
  taskCode?: string | null;
  /** Nombre corto operativo de la tarea. */
  title?: string | null;
  assigneeName?: string | null;
  /** Ubicación/lugar real (nivel 5). Null = se omite. */
  location?: string | null;
  /** Descripción (nivel 6, siempre la primera sacrificable). */
  description?: string | null;
}

const clean = (v?: string | null): string | null => {
  const s = (v ?? '').trim();
  return s.length > 0 ? s : null;
};

/** Trunca por caracteres respetando palabras; sin sufijo si ya cabe. */
export function truncateText(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  const cut = text.slice(0, maxChars);
  const stop = cut.lastIndexOf(' ');
  return `${(stop > maxChars * 0.6 ? cut.slice(0, stop) : cut).trimEnd()}…`;
}

export interface TaskVisualHierarchyProps {
  task: TaskHierarchyData;
  variant?: TaskHierarchyVariant;
  /** Máx. caracteres de la descripción antes de truncar (COMPACT por defecto 90). */
  maxDescriptionChars?: number;
  className?: string;
}

export function TaskVisualHierarchy({
  task,
  variant = 'full',
  maxDescriptionChars,
  className,
}: TaskVisualHierarchyProps): React.ReactElement {
  const project = clean(task.projectName);
  const area = clean(task.processName);
  const code = clean(task.taskCode);
  const title = clean(task.title);
  const responsible = clean(task.assigneeName);
  const location = clean(task.location);
  const description = clean(task.description);

  const showResponsible = variant === 'full' || variant === 'compact';
  const showLocation = variant === 'full' || variant === 'compact';
  const showDescription = variant === 'full' || variant === 'compact';
  const descMax = maxDescriptionChars ?? (variant === 'compact' ? 90 : 240);

  return (
    <div
      className={className}
      data-testid="task-visual-hierarchy"
      data-variant={variant}
    >
      {/* 1 · PROJECT — máxima prioridad visual */}
      {project && (
        <div
          data-testid="hierarchy-project"
          className="truncate text-[11px] font-semibold uppercase tracking-wide text-primary"
        >
          {project}
        </div>
      )}
      {/* 2 · AREA / PROCESS — segunda jerarquía */}
      {area && (
        <div
          data-testid="hierarchy-area"
          className="truncate text-[11px] font-medium uppercase tracking-wide text-outline-variant"
        >
          {area}
        </div>
      )}
      {/* 3 · SHORT TASK NAME — identificación operativa */}
      {title && (
        <div data-testid="hierarchy-task" className="truncate text-sm font-medium text-on-surface">
          {code ? <span className="text-outline-variant">{code} · </span> : null}
          {title}
        </div>
      )}
      {/* 4 · RESPONSIBLE */}
      {showResponsible && responsible && (
        <div data-testid="hierarchy-responsible" className="truncate text-xs text-on-surface-variant">
          {responsible}
        </div>
      )}
      {/* 5 · LOCATION — solo si existe */}
      {showLocation && location && (
        <div data-testid="hierarchy-location" className="truncate text-xs text-on-surface-variant">
          {location}
        </div>
      )}
      {/* 6 · DESCRIPTION — primera sacrificable */}
      {showDescription && description && (
        <div
          data-testid="hierarchy-description"
          className="truncate text-xs text-outline-variant"
          title={description}
        >
          {truncateText(description, descMax)}
        </div>
      )}
    </div>
  );
}

export default TaskVisualHierarchy;
